/**
 * Detects a new deployment from the static build manifest Nuxt already ships
 * (`/_nuxt/builds/latest.json`, served by the CDN): never an SSR or API route, so a
 * check costs no function invocation. The popin lives in components/AppUpdateNotice.vue.
 *
 * The auto-refresh countdown (10 s, cancellable) lives in the popin component.
 *
 * Checked shortly after load, every 5 minutes, and on focus, online and when the
 * tab becomes visible again. Hidden or offline tabs skip the check.
 */
const CHECK_INTERVAL_MS = 5 * 60_000
const MIN_GAP_MS = 20_000

export default defineNuxtPlugin((nuxtApp) => {
  if (import.meta.dev) return

  const state = useAppUpdateState()
  const autoRefresh = useState('site-auto-refresh', () => false)
  const config = useRuntimeConfig()
  const currentId = String(config.app.buildId || '')
  const base = `${config.app.baseURL.replace(/\/$/, '')}/${config.app.buildAssetsDir.replace(/^\/|\/$/g, '')}`

  const isBackOffice = () => /^(\/[a-z]{2})?\/(app|dashboard)(\/|$)/.test(window.location.pathname)

  // Back-office refreshes by itself unless it was switched off; the public site never does unless asked.
  try {
    const saved = localStorage.getItem(AUTO_REFRESH_KEY)
    autoRefresh.value = saved === null ? isBackOffice() : saved === '1'
  } catch {
    autoRefresh.value = isBackOffice()
  }

  const markAvailable = (id: string) => {
    if (!id || !currentId || id === currentId) return
    state.value = { available: true, latestId: id }
  }

  let lastCheck = 0
  let checking = false
  const check = async () => {
    if (document.visibilityState !== 'visible' || !navigator.onLine) return
    if (state.value.available) return
    if (checking || Date.now() - lastCheck < MIN_GAP_MS) return
    checking = true
    lastCheck = Date.now()
    try {
      const response = await fetch(`${base}/builds/latest.json?t=${Date.now()}`, { cache: 'no-store' })
      if (response.ok) markAvailable(String((await response.json())?.id || ''))
    } catch {
      // Offline or file missing: stay silent, try again later.
    } finally {
      checking = false
    }
  }

  nuxtApp.hook('app:manifest:update', (meta?: { id?: string }) => markAvailable(meta?.id || ''))
  nuxtApp.hook('page:finish', () => { markFormDirty(false) })
  window.addEventListener('focus', () => { void check() })
  window.addEventListener('online', () => { void check() })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check()
  })
  window.setTimeout(() => { void check() }, 8_000)
  window.setInterval(() => { void check() }, CHECK_INTERVAL_MS)

  // Unsaved input in a real form counts as edit mode (search boxes do not).
  document.addEventListener('input', (event) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('form') || target?.tagName === 'TEXTAREA') markFormDirty(true)
  }, true)
  document.addEventListener('submit', () => markFormDirty(false), true)
})
